/**
 * =============================
 * Save Design Center Tabletops
 * =============================
 * Includes:
 * - Save API Request
 * - Save pending changes in sessionStorage
 * - Prompt for unsaved changes
 * - Edit tabletop title
 */
import productFetch from './product-fetch';
import { clearNotifications, addNotification } from './table-notifications';

const saveBtn = document.querySelector('#design-save-btn');
const formTitle = document.querySelector('#design-title');
const itemInputs = document.querySelectorAll('input.table-field');
const editTitleBtn = document.querySelector("#edit-design-title");
const saveWrapper = document.querySelector("#design-save");

// Stores current time in milliseconds, to see how much time the save AJAX method has taken
let saveStartTime = 0;

/**
 * Set up all event listeners on tabletop
 */
const bindTableActions = async () => {
	bindTableSave();
	bindTableTitleEdit();
};

/**
 * Navigate to individual tabletop page
 */
const goToDesign = async (link) => {
	window.location.assign(link);
};

/**
 * Whether there are any unsaved (pending) changes on the tabletop
 * @return  {boolean}
 */
const hasUnsavedChanges = () => {
	const pendingItems = sessionStorage.getItem('pendingItems');
	const pendingTitle = sessionStorage.getItem('pendingTitle');
	const pendingExistingId = sessionStorage.getItem('pendingExistingId');

	// No data at all
	if (pendingItems === null && pendingTitle === null){
		return false;
	}
	
	// Existing tabletop edit: pending changes don't match the tabletop ID. Ignore them.
	if (dcRestSettings.isTablePost && pendingExistingId === null){
		return false;
	}
	if (dcRestSettings.isTablePost && pendingExistingId.toString() !== dcRestSettings.cpid.toString()){
		return false;
	}

	// There is new pending data in one of the session items.
	return true;
}

// Browser prompt: "Leave Site? Changes you made may not be saved."
window.addEventListener('beforeunload', (event) => {
	const tableHasImages = document.querySelectorAll('.table-item--has-image');

	// Skip prompt in these situations
	if (
		!hasUnsavedChanges() || 
		saveBtn.classList.contains('logging-in') ||
		document.body.classList.contains('.go-to-design')
	) {
		return;
	}

	// TRIGGER PROMPT
	// If you prevent default behavior, Firefox shows prompt.
	event.preventDefault();
	// Chrome requires returnValue to be set.
	event.returnValue = null;
});

/**
 * Show custom confirmation modal prompting user to log in to their account.
 * @param   {string}  messageText
 */
const confirmText = async (messageText) => {
	const footer = document.querySelector('#footer');
	const confBg = document.createElement('div');
	const confDialog = document.createElement('div');
	const confText = document.createElement('p');
	confBg.className = 'confirm-dialog__bg';
	confDialog.className = 'confirm-dialog';
	confText.className = 'has-small-font-size';
	confText.textContent = messageText;

	confBg.appendChild(confDialog);
	confDialog.appendChild(confText);

	confDialog.insertAdjacentHTML(
		'beforeend',
		'<button class="button is-style-btn-small" id="yes">Log In or Sign Up</button><button class="button is-style-btn-small-outline" id="no">Close</button>'
	);

	footer.after(confBg);

	document.body.classList.add('confirmation-active');
};

/**
 * Change text of save button, as it is saving
 * "Saving..."" -> "Saved!"" -> "Save" (default)
 * Re-enables button when complete.
 */
const animateSaveBtn = async () => {
	let initialDelay = 500;
	if (saveStartTime > 0){
		// Minimum of 500 sec delay. 
		// If save request has already taken longer than that, don't add extra time to that delay
		initialDelay = Math.max(0, ((saveStartTime + initialDelay) - Date.now()));
		initialDelay = Math.min(initialDelay, 500);
	}

	saveBtn.innerText = 'Saving...';

	let saving = function () {
		setTimeout(function () {
			saveBtn.innerText = 'Saved!';
			saved();
		}, initialDelay + 1);
	};

	let saved = function () {
		setTimeout(function () {
			saveBtn.innerText = 'Save';
			saveBtn.removeAttribute("disabled");

			updateTableTitle();

			// Toggle click to close editing
			const titleEle = document.querySelector('#edit-design-title.is-editing');
			if (titleEle !== null){
				titleEle.click();
			}
		}, initialDelay + 400);
	};

	saving();
};

/**
 * Set up event listeners on tabletop
 */
const bindTableSave = async () => {
	if (!saveBtn) {
		return;
	}

	saveBtn.addEventListener(
		'click',
		(e) => {
			e.stopPropagation();
			saveDesign();
		},
		false
	);
};

/**
 * Create new tabletop post, and redirect to its page (e.g. "/table-top/test3/")
 */
const newDesign = async (postContent) => {
	// Create new tabletop (post) via API
	const newPost = await productFetch('wp/v2/table-top', {
		credentials: 'same-origin',
		method: 'POST',
		body: JSON.stringify(postContent),
		headers: new Headers({
			'Content-Type': 'application/json',
			'X-WP-Nonce': dcRestSettings.nonce
		})
	});

	// Clear any pending tabletop data stored in session
	sessionStorage.clear();

	// Redirect to single post page for newly created tabletop design
	document.body.classList.add('.go-to-design');
	await goToDesign(newPost.link);
};

/**
 * Create object containing all tabletop data
 * @return  {Object}  Object with keys corresponding to "data-var" values on table item inputs. Values are the products that are selected.
 */
export const getTableItemsData = () => {
	let tableItems = {};

	if (itemInputs === null){
		console.error('Input field elements containing tabletop data are missing!');
	}

	// Get value in each table item slot (stored in hidden input)
	itemInputs.forEach((item) => {
		const catKey = item.getAttribute('data-var');
		const catVal = item.value;

		if (catVal > 0) {
			tableItems[catKey] = catVal;
		}
	});

	return tableItems;
}

/**
 * Save pending/unsaved changes in sessionStorage.
 * @param   {Object}  existingTableData  (Optional) Pass in table data object instead of creating it
 * @return  {void}
 */
export const savePendingData = (existingTableData = null) => {
	// Get object with value of each table item slot
	let tableItems;
	if (existingTableData !== null){
		tableItems = existingTableData;
	} else {
		tableItems = getTableItemsData();
	}

	// Save tabletop data and title in browser's sessionStorage.
	sessionStorage.setItem('pendingItems', JSON.stringify(tableItems));

	if (formTitle !== null){
		sessionStorage.setItem('pendingTitle', formTitle.value);
	}

	if (dcRestSettings.isTablePost){
		// If there is a current tabletop, Save its ID
		sessionStorage.setItem('pendingExistingId', dcRestSettings.cpid)
	} else {
		// Clear value for new, unsaved tabletops
		sessionStorage.removeItem('pendingExistingId');
	}

	return;
}

/**
 * Save design via AJAX
 */
const saveDesign = async () => {
	saveStartTime = Date.now();
	clearNotifications();

	// Get object with value of each table item slot
	const tableItems = getTableItemsData();

	if (dcRestSettings.cuid < 1) {
		// User is logged out ---
		confirmText('You must be logged in to save your design.');

		// Save tabletop and title in browser's sessionStorage.
		savePendingData(tableItems);

		const confBg = document.querySelector('.confirm-dialog__bg');
		const confDialog = confBg.querySelector('.confirm-dialog');

		// Confirm Dialog: Log in or Cancel
		confDialog.addEventListener('click', async (event) => {
			event.stopPropagation();

			// Log in or sign up
			if (event.target.matches('#yes')) {
				saveBtn.classList.add('logging-in');
				confBg.remove();
				window.location.assign('/my-account'); // Login link with redirect_to param set to current page
			}
	
			// Cancel
			if (event.target.matches('#no')) {
				confBg.remove();
			}
		});
	} 
	else {
		// User is logged in ----
		saveBtn.innerText = 'Saving...';
		saveBtn.setAttribute("disabled", "disabled");

		const postContent = {
			title: (formTitle.value !== null && formTitle.value.length > 0) ? formTitle.value : 'Untitled Design',
			meta: { table_items: JSON.stringify(tableItems) },
			status: 'publish',
			password: '',
			content: '<!-- wp:block {"ref":117436} /-->'
		};
	
		if (dcRestSettings.isTablePost && Number(dcRestSettings.cuid) === Number(dcRestSettings.cpaid)) {
			// Existing design: update post data
			// Uses the standard WordPress REST API
			await productFetch(`wp/v2/table-top/${dcRestSettings.cpid}`, {
				credentials: 'same-origin',
				method: 'POST',
				body: JSON.stringify(postContent),
				headers: new Headers({
					'Content-Type': 'application/json',
					'X-WP-Nonce': dcRestSettings.nonce
				})
			});

			// Clear any pending tabletop data stored in session
			sessionStorage.clear();

			// Animate: "Saving.." on button
			await animateSaveBtn();
		} else {
			// This is a new tabletop design. Save it to a newly created post.
			postContent.title += ' - Customized';
			await newDesign(postContent);
		}
	}
};


/**
 * Click event to edit title of the tabletop
 */
const bindTableTitleEdit = async () => {
	if (!editTitleBtn || !saveWrapper) {
		return;
	}

	editTitleBtn.addEventListener(
		'click',
		(event) => {
			event.preventDefault(); // prevent any scrolling

			// Toggle class to show/hide title input
			if (saveWrapper.classList.contains('is-editing')){
				saveWrapper.classList.remove('is-editing');
			} else {
				saveWrapper.classList.add('is-editing');
			}

			// Also toggle class on clicked element itself
			if (event.currentTarget !== null){
				if (event.currentTarget.classList.contains('is-editing')){
					event.currentTarget.classList.remove('is-editing');
				} else {
					event.currentTarget.classList.add('is-editing');
				}
			}
		},
		false
	);
};

/**
 * Update table title heading to match value of input (used after save)
 */
const updateTableTitle = () => {
	const currentTitle = document.querySelector('#design-title').value;
	document.querySelector('#edit-design-title .title-text').innerText = currentTitle;
};

export default bindTableActions;
